import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { contractor360KAETabs, getDraftSite, IContractorSites } from '.';
import { useLocation, useNavigate } from 'react-router-dom';
import { Wrapper } from '@components/common/Wrapper/wrapper';
import useStepperStore from '@store/stepperStore';
import { Site } from './site';
import { SiteSummaryDetails } from '@services/hooks/contractor360';
import useSiteLeadStore from '@store/siteLeadStore';
import { Modal } from '@components/common/modal/modal';
import { SixMonthsConfirmationModal } from '@components/contractor360/components';
import { useEndUserProfileStore } from '@store/endUserProfileStore';
import { useEndUserProfileService } from '@services/hooks/endUserProfile';
import { ToastType, useTosterStore } from '@store/toster';
import { overlayRemoveFadeAction } from '@components/common/sort';
import { TabState } from '@components/common/tab-state/tabState';
import { Factory } from './factory';
import { NoDataFound } from '@components/common/no-data-found/noDataFound';
import { ProductRecommendationModal } from '../productReommendation/productRecommendation';
import { useHandleProductRecommendation } from '@components/contractor360/components/productReommendation';
import { ContractorAidModals } from '@components/contractor360KAE/contractorAidModals';
import { useComponentLevelAccessCheck } from '../../../../authGuard/hooks/useComponentLevelAccessCheck';
import { getSortedLastVisitedSites } from '@helper/utils';
import { useSiteAllDetailsStore } from '@store/siteAllDetailsStore';
import { useAddNewSiteStore } from '@store/lms/addNewSiteStore';
import { useUserRoleType } from '@hooks/useUserRoleType';
import { useSiteVisitRecordingUploadStore } from '@store/siteVisitRecordingUploadStore';
import { SiteVisitRecordingConsentModal } from '@components/siteLeads/siteVisitRecording/SiteVisitRecordingConsentModal';
import { isSiteVisitRecordingActive, useSiteVisitRecordingStore } from '@store/siteVisitRecordingStore';
import { PROVISIONAL_NEW_SITE_RECORDING_ID } from '@services/siteVisitRecording/siteVisitRecordingConstants';
import { useIsImpersonationLoginUser } from '../../../../authGuard/hooks/useIsImpersonationLoginUser';
import { isRecordingAccessBde } from '@config/constant';
import { discardActiveSiteVisitRecording } from '@services/siteVisitRecording/siteVisitRecordingEngine';
import { useGetRxDb } from '@hooks/getRxdbData';
import { ProfileData } from '@services/hooks/profile';

export const ContractorSites = (props: IContractorSites): ReactElement => {
	const navigate = useNavigate();
	const { state: locationState } = useLocation();
	const checkTabState = useComponentLevelAccessCheck('tab-state');
	const { clearSiteDetails } = useSiteAllDetailsStore();
	const { resetAddNewSiteValues } = useAddNewSiteStore();
	const checkFactoryTabState = useComponentLevelAccessCheck('factory-tab');
	const role = useUserRoleType();
	const isBDERole = role === 'BDE';
	const { result: profile } = useGetRxDb<ProfileData>('profile');
	const mdiCode = profile[0]?.mdicode_c;
	const clearPendingRecording = useSiteVisitRecordingUploadStore((s) => s.clearPendingRecording);
	const recordingPhase = useSiteVisitRecordingStore((s) => s.phase);
	const [isRecordingConsentOpen, setIsRecordingConsentOpen] = useState(false);
	const pendingAutoStartRecordingRef = useRef(false);

	const isImpersonationLoginUser = useIsImpersonationLoginUser();

	const {
		data,
		isGetContractorDetailFetching,
		error,
		contractorDetailsRefetch,
		allSites,
		isSiteSummaryFetching,
		allFactoryVisits,
		showCarpenterModal,
		siteVisitRecordingConsentSource,
	} = props;
	const lmsUserEnable = data?.source_c === 'LMS';
	const { setCurrentStep } = useStepperStore();
	const { setSiteSfid, setSiteLeadId, setKeyBaseValue } = useSiteLeadStore();
	const {
		isConformationNeeded,
		setKeyBaseValue: setEndUserKeyBaseValue,
		openConformationModal: isOpen,
		isAIDOpen,
		isNoOfCarpenterOpen,
		setContractorAidModals,
		resetContractorAidModals,
	} = useEndUserProfileStore();
	const { refetch: endUserProfileService } = useEndUserProfileService(data?.id);
	const { setToaster } = useTosterStore();

	const [activeTab, setActiveTab] = useState<number>(0);
	const handleTabClick = useCallback((index: number) => {
		setActiveTab(index);
	}, []);

	const tabsForRender = useMemo(() => {
		return checkFactoryTabState
			? contractor360KAETabs
			: contractor360KAETabs.filter((tab) => tab.name !== 'Factory Visit');
	}, [checkFactoryTabState]);

	useEffect(() => {
		if (activeTab > tabsForRender.length - 1) {
			setActiveTab(0);
		}
	}, [activeTab, tabsForRender]);

	const {
		handleProductRecommendation,
		isProductRecommendationOpen,
		productRecommendationData,
		closeProductRecommendation,
	} = useHandleProductRecommendation({
		membershipno: data?.membershipno,
		contractorclass: data?.contractorclass,
		banking_mtd_kgs: data?.banking_mtd_kgs,
	});

	const handleEndUserProfileVerificationStage = (fn: () => void): void => {
		if (isConformationNeeded && data?.id && endUserProfileService) {
			endUserProfileService()
				.then((response) => {
					if (response.isError) {
						setToaster(true, ToastType.error, 'Please contact admin for enrolled details updates.');
						return;
					}
					setEndUserKeyBaseValue('basicDetailsRevalidate', response?.data?.data?.basic);
					setEndUserKeyBaseValue('moreDetailsRevalidate', false);
					setEndUserKeyBaseValue('openConformationModal', true);
				})
				.catch(() => {
					setToaster(true, ToastType.error, 'Please contact admin for enrolled details updates.');
				});
			return;
		}
		fn();
	};

	const handleSiteDataUpdate = (autoStartRecording = true): void => {
		if (data?.id) {
			const shouldShowModal = setContractorAidModals({
				is_aid_c: data.is_aid_c,
				no_of_carpenters: data.no_of_carpenters,
				glue_consumption_per_month: data.glue_consumption_per_month,
				showCarpenterModal: showCarpenterModal ?? false,
			});
			if (shouldShowModal) return;
		}

		clearPendingRecording();
		setSiteSfid('');
		setSiteLeadId(null);
		setKeyBaseValue('addNewSiteLead', false);
		setKeyBaseValue('siteDetails', {
			name: data?.name,
			number: data?.phone,
			sfid: data?.sfid,
			membershipno: data?.membershipno,
			contractorclass: data?.contractorclass,
		});
		navigate('/add-new-site', {
			state: {
				siteId: null,
				...(autoStartRecording
					? {
							newSiteRecording: true,
							autoStartRecording: true as const,
							provisionalSiteId: PROVISIONAL_NEW_SITE_RECORDING_ID,
						}
					: {}),
			},
		});
		pendingAutoStartRecordingRef.current = false;
	};

	const handleSiteRedirection = (autoStartRecording = true): void => {
		setCurrentStep(0);
		handleSiteDataUpdate(autoStartRecording);
	};

	const checkHandleAddNewSite = async (autoStartRecording = true): Promise<void> => {
		handleEndUserProfileVerificationStage(() => {
			handleProductRecommendation(() => handleSiteRedirection(autoStartRecording));
		});
	};

	const checkHandleSingleSiteRedirection = (fn: () => void): void => {
		handleEndUserProfileVerificationStage(fn);
	};

	const hasRecordingConsent = isRecordingAccessBde(mdiCode) && Boolean(data?.consent);

	const handleAddNewSite = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
		e.stopPropagation();
		clearSiteDetails();
		resetAddNewSiteValues();
		setKeyBaseValue('hasVisitRecordingConsent', hasRecordingConsent);

		const goWithoutRecording = (): void => {
			pendingAutoStartRecordingRef.current = false;
			checkHandleAddNewSite(false).catch(() => {});
		};

		if (hasRecordingConsent) {
			if (isSiteVisitRecordingActive(recordingPhase)) {
				goWithoutRecording();
				return;
			}
			setIsRecordingConsentOpen(true);
			return;
		}

		// Consent false: wipe local chunks/pending and never upload for this visit.
		void discardActiveSiteVisitRecording().finally(goWithoutRecording);
	};

	const handleRecordingConsentAcknowledge = (): void => {
		setIsRecordingConsentOpen(false);
		pendingAutoStartRecordingRef.current = true;
		checkHandleAddNewSite(true).catch(() => {});
	};

	const handleAddNewFactoryVisit = useCallback(() => {
		if (data?.id) {
			const shouldShowModal = setContractorAidModals({
				is_aid_c: data?.is_aid_c,
				no_of_carpenters: data?.no_of_carpenters,
				glue_consumption_per_month: data.glue_consumption_per_month,
				showCarpenterModal: showCarpenterModal ?? false,
			});
			if (shouldShowModal) return;
		}

		navigate('/factory-visit/add-factory-visit', {
			state: {
				membershipNo: data?.membershipno,
			},
		});
	}, [
		data?.id,
		data?.is_aid_c,
		data?.no_of_carpenters,
		data?.glue_consumption_per_month,
		data?.membershipno,
		showCarpenterModal,
		navigate,
		setContractorAidModals,
	]);

	const sortedSites = useMemo((): SiteSummaryDetails[] => {
		if (!Array.isArray(allSites)) return [];

		const filteredSites = isBDERole
			? allSites.filter((site) => {
					const siteWithStatus = site as SiteSummaryDetails & { site_current_status_c?: string };
					return siteWithStatus?.site_current_status_c !== 'Upcoming';
				})
			: allSites;

		const sortedByCreatedDate = [...filteredSites].sort((a, b) => {
			const dateA = a?.createddate ? new Date(a.createddate) : null;
			const dateB = b?.createddate ? new Date(b.createddate) : null;

			if (!dateA) return 1;
			if (!dateB) return -1;

			return dateB.getTime() - dateA.getTime();
		});
		const draftSites = sortedByCreatedDate.filter(getDraftSite);
		const nonDraftSites = sortedByCreatedDate.filter((site) => !getDraftSite(site));

		return [...draftSites, ...nonDraftSites];
	}, [allSites, isBDERole]);

	const sortedLastVisitedSites = useMemo((): SiteSummaryDetails[] => {
		const sortedSites = getSortedLastVisitedSites(allSites);

		// Filter out sites with primary_cmdi value for BDE role only
		if (isBDERole) {
			return sortedSites.filter((site) => {
				const siteWithPrimaryCmdi = site as SiteSummaryDetails & { primary_cmdi?: string | null };
				return !siteWithPrimaryCmdi?.primary_cmdi;
			});
		}

		return sortedSites;
	}, [allSites, isBDERole]);

	const closeEndUserModal = useCallback(() => {
		overlayRemoveFadeAction(() => setEndUserKeyBaseValue('openConformationModal', false));
	}, [setEndUserKeyBaseValue]);

	const closeAIDModal = useCallback(() => {
		overlayRemoveFadeAction(() => setEndUserKeyBaseValue('isAIDOpen', false));
	}, [setEndUserKeyBaseValue]);

	const closeCarpenterModal = useCallback(() => {
		overlayRemoveFadeAction(() => setEndUserKeyBaseValue('isNoOfCarpenterOpen', false));
	}, [setEndUserKeyBaseValue]);

	useEffect(() => {
		resetContractorAidModals();
	}, [resetContractorAidModals]);

	const renderSites = (): ReactElement => (
		<ul className="sites-card-list row">
			{allSites?.length ? (
				sortedSites?.map((site, index) => (
					<Site
						handleEndUserProfileVerificationStage={checkHandleSingleSiteRedirection}
						key={site?.id}
						data={data}
						site={site}
						siteIndex={index}
						membershipno={data?.membershipno}
						contractor_name={data?.name}
						contractorclass={data?.contractorclass}
						siteType="all"
						showCarpenterModal={showCarpenterModal}
						lmsUserEnable={lmsUserEnable}
					/>
				))
			) : (
				<NoDataFound />
			)}
		</ul>
	);

	const renderFactories = (): ReactElement => (
		<ul className="sites-card-list row">
			{allFactoryVisits?.length ? (
				allFactoryVisits?.map((factory, index) => (
					<Factory
						key={factory?.id}
						factory={factory}
						factoryIndex={index}
						membershipno={factory.membership_number_c}
						contractor_name={factory.contractor_name}
					/>
				))
			) : (
				<NoDataFound />
			)}
		</ul>
	);

	const renderLastVisitedSites = (): ReactElement => (
		<ul className="sites-card-list row">
			{allSites?.length ? (
				sortedLastVisitedSites?.map((site, index) => (
					<Site
						handleEndUserProfileVerificationStage={checkHandleSingleSiteRedirection}
						key={site?.id}
						data={data}
						site={site}
						siteIndex={index}
						membershipno={data?.membershipno}
						contractor_name={data?.name}
						siteType="lastVisited"
						showCarpenterModal={showCarpenterModal}
						checkKAEPermission={!checkFactoryTabState}
						lmsUserEnable={lmsUserEnable}
					/>
				))
			) : (
				<NoDataFound />
			)}
		</ul>
	);

	const isTabStateActive = checkTabState;

	const renderContent = (): ReactElement => {
		if (isTabStateActive) {
			if (activeTab === 0) return renderLastVisitedSites();
			if (activeTab === 1) return renderSites();
			return renderFactories();
		}

		return activeTab === 0 ? renderSites() : renderFactories();
	};

	return (
		<>
			<section className="contractor-sites">
				<div className="container">
					<div className="contractor-sites-inner">
						{isTabStateActive ? (
							<TabState
								dataArray={tabsForRender}
								activeTab={activeTab}
								tabClick={handleTabClick}
								addNewSite={handleAddNewSite}
								addNewFactoryVisit={handleAddNewFactoryVisit}
								isGetContractorDetailFetching={isGetContractorDetailFetching}
							/>
						) : (
							<div className="sites-title">
								<span className="h3">
									Sites<span className="h5">(6 months)</span>
								</span>
								{!isImpersonationLoginUser && (
									<button
										type="button"
										id="site-list-btn"
										aria-label="add-new-site-button"
										className="btn btn-secondary btn-small add-new-site-btn"
										value="add-new-site-button"
										onClick={handleAddNewSite}
										disabled={isGetContractorDetailFetching}
									>
										Add New Site{' '}
										<span className="add-new-site-icon">
											<span className="icon-plus"></span>
										</span>
									</button>
								)}
							</div>
						)}
						<Wrapper isLoaderVisible={isGetContractorDetailFetching || isSiteSummaryFetching} isError={error}>
							{renderContent()}
						</Wrapper>
					</div>
				</div>
			</section>
			{isOpen && (
				<Modal
					modalOpen={isOpen}
					modalId={'closeLeads'}
					onModalClose={closeEndUserModal}
					className="dialog-sm close-site-popup dialog-conform"
				>
					<SixMonthsConfirmationModal
						message={'Kindly review and confirm the highlighted enrollment data.'}
						data={data}
						modalClose={closeEndUserModal}
					/>
				</Modal>
			)}
			{isProductRecommendationOpen.modalOpen && (
				<Modal
					modalTitle={'Product Recommendation'}
					modalOpen={isProductRecommendationOpen.modalOpen}
					modalId={'productRecommendation'}
					onModalClose={closeProductRecommendation}
					className="dialog-xxl dialog-bottom close-site-popup dialog-conform"
				>
					<ProductRecommendationModal
						modalClose={closeProductRecommendation}
						onSubmit={() => {
							const shouldAutoStart = pendingAutoStartRecordingRef.current;
							closeProductRecommendation();
							handleSiteRedirection(shouldAutoStart);
						}}
						membershipNo={data?.membershipno}
						productRecommendationData={productRecommendationData}
						isUpdate={isProductRecommendationOpen.isUpdate}
					/>
				</Modal>
			)}

			<Modal
				modalOpen={isRecordingConsentOpen}
				modalId="contractor360SiteVisitRecordingInfo"
				onModalClose={() => setIsRecordingConsentOpen(false)}
				className="dialog-sm opt-confirm pt-25"
			>
				<SiteVisitRecordingConsentModal
					onAcknowledge={handleRecordingConsentAcknowledge}
					onDismiss={() => setIsRecordingConsentOpen(false)}
				/>
			</Modal>

			{data?.id && (
				<ContractorAidModals
					showCarpenterModal={showCarpenterModal}
					userId={data.id}
					isAIDOpen={isAIDOpen}
					isNoOfCarpenterOpen={isNoOfCarpenterOpen}
					closeAIDModal={closeAIDModal}
					closeCarpenterModal={closeCarpenterModal}
					refetch={contractorDetailsRefetch ?? ((): void => {})}
				/>
			)}
		</>
	);
};
